import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';
import UserDetailModal from './UserDetailModal';

const AdminRegularUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [actionLoading, setActionLoading] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);

    const fetchUsers = useCallback(async (query) => {
        setLoading(true);
        try {
            const params = query ? { params: { search: query } } : {};
            const res = await api.get(API_CONFIG.ADMIN.USERS_REGULAR, params);
            if (res.data.status === 'success') {
                setUsers(res.data.users);
            }
        } catch (err) {
            setError('Failed to fetch regular users');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleSearch = (e) => {
        e.preventDefault();
        fetchUsers(search);
    };

    const handleToggleStatus = async (id) => {
        setActionLoading(`toggle-${id}`);
        try {
            const res = await api.put(`/admin/users/users/${id}/toggle-status`);
            if (res.data.status === 'success') {
                setSuccess(res.data.message);
                fetchUsers(search);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to toggle user status');
        } finally {
            setActionLoading(null);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
            return;
        }
        setActionLoading(`delete-${id}`);
        try {
            const res = await api.delete(`/admin/users/users/${id}`);
            if (res.data.status === 'success') {
                setSuccess('User deleted successfully');
                fetchUsers(search);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to delete user');
        } finally {
            setActionLoading(null);
        }
    };

    return (
        <div className="p-6">
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Regular Users</h1>
                    <p className="text-gray-600">Manage regular users from users table</p>
                </div>
            </div>

            {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">{error}</div>
            )}
            {success && (
                <div className="mb-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded">{success}</div>
            )}

            <form onSubmit={handleSearch} className="mb-4 flex gap-2">
                <input
                    type="text"
                    placeholder="Search by name, email, phone, or username..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                >
                    Search
                </button>
                {search && (
                    <button
                        type="button"
                        onClick={() => { setSearch(''); fetchUsers(); }}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm font-medium"
                    >
                        Clear
                    </button>
                )}
            </form>

            <div className="bg-white shadow sm:rounded-lg overflow-hidden">
                {loading ? (
                    <p className="text-gray-500 text-center py-8">Loading...</p>
                ) : users.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No regular users found.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Verified</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {users.map(user => (
                                    <tr key={user.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm font-medium">
                                            <button
                                                onClick={() => setSelectedUser(user.id)}
                                                className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                                            >
                                                {user.fullname || '-'}
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600">{user.email || '-'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-600">{user.phone_number || '-'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-600">{user.username || '-'}</td>
                                        <td className="px-4 py-3 text-sm">
                                            <span className={`px-2 py-1 text-xs font-medium rounded ${user.is_email_verified ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                                {user.is_email_verified ? 'Yes' : 'No'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                            <span className={`px-2 py-1 text-xs font-medium rounded ${user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {user.is_active ? 'Active' : 'Disabled'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-right space-x-2">
                                            <button
                                                onClick={() => handleToggleStatus(user.id)}
                                                disabled={actionLoading === `toggle-${user.id}`}
                                                className={`inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded ${user.is_active ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' : 'bg-green-100 text-green-800 hover:bg-green-200'} disabled:opacity-50`}
                                            >
                                                {user.is_active ? 'Disable' : 'Enable'}
                                            </button>
                                            <button
                                                onClick={() => handleDelete(user.id)}
                                                disabled={actionLoading === `delete-${user.id}`}
                                                className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded bg-red-100 text-red-800 hover:bg-red-200 disabled:opacity-50"
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 text-sm text-gray-500">
                    Total: {users.length} user{users.length !== 1 ? 's' : ''}
                </div>
            </div>
            {selectedUser && (
                <UserDetailModal
                    table="users"
                    userId={selectedUser}
                    onClose={() => setSelectedUser(null)}
                />
            )}
        </div>
    );
};

export default AdminRegularUsers;